import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, ArrowLeft, Calendar, Clock, Video, Link2, Monitor } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateLiveClass() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_id: '',
    scheduled_at: '',
    duration_minutes: '60',
    stream_type: 'external',
    external_link: '',
    enable_chat: true,
    enable_screen_share: true
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`, getAuthHeader());
      setCourses(response.data);
    } catch (error) {
      toast.error('Failed to load courses');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate external link if external type selected
    if (formData.stream_type === 'external' && !formData.external_link) {
      toast.error('Please provide a meeting link (Zoom/Google Meet/YouTube)');
      setLoading(false);
      return;
    }

    try {
      await axios.post(
        `${API}/live-classes`,
        { 
          ...formData, 
          duration_minutes: parseInt(formData.duration_minutes),
          external_link: formData.stream_type === 'external' ? formData.external_link : null
        },
        getAuthHeader()
      );
      toast.success('Live class scheduled successfully!');
      navigate('/teacher/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule class');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/teacher/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-2">Schedule Live Class</h1>
          <p className="text-lg text-slate-600">Set up a new live class session for your students</p>
        </div>

        <Card className="bg-white rounded-xl border border-slate-100 shadow-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Class Title */}
            <div>
              <Label htmlFor="title" className="text-slate-700 font-medium text-base mb-2 block">
                Class Title *
              </Label>
              <Input
                id="title"
                data-testid="class-title-input"
                type="text"
                placeholder="e.g., Algebra Fundamentals - Chapter 5"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="h-12 rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 bg-slate-50 focus:bg-white transition-all text-slate-900"
                required
              />
            </div>

            {/* Class Description */}
            <div>
              <Label htmlFor="description" className="text-slate-700 font-medium text-base mb-2 block">
                Description *
              </Label>
              <Textarea
                id="description"
                data-testid="class-description-input"
                placeholder="What will be covered in this class?"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="min-h-[100px] rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 bg-slate-50 focus:bg-white transition-all text-slate-900"
                required
              />
            </div>

            {/* Select Course */}
            <div>
              <Label htmlFor="course" className="text-slate-700 font-medium text-base mb-2 block">
                Select Course *
              </Label>
              <Select
                value={formData.course_id}
                onValueChange={(value) => setFormData({ ...formData, course_id: value })}
                required
              >
                <SelectTrigger data-testid="course-select" className="h-12 rounded-lg border-slate-200 focus:border-indigo-500 bg-slate-50 text-slate-900">
                  <SelectValue placeholder="Choose a course" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {courses.map((course) => (
                    <SelectItem key={course.id} value={course.id} className="text-slate-900">
                      {course.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="scheduled_at" className="text-slate-700 font-medium text-base mb-2 block">
                  Scheduled Date & Time *
                </Label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="scheduled_at"
                    data-testid="scheduled-datetime-input"
                    type="datetime-local"
                    value={formData.scheduled_at}
                    onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                    className="pl-12 h-12 rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 bg-slate-50 focus:bg-white transition-all text-slate-900"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="duration" className="text-slate-700 font-medium text-base mb-2 block">
                  Duration (minutes) *
                </Label>
                <div className="relative">
                  <Clock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Select
                    value={formData.duration_minutes}
                    onValueChange={(value) => setFormData({ ...formData, duration_minutes: value })}
                  >
                    <SelectTrigger data-testid="duration-select" className="pl-12 h-12 rounded-lg border-slate-200 focus:border-indigo-500 bg-slate-50 text-slate-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="30" className="text-slate-900">30 minutes</SelectItem>
                      <SelectItem value="45" className="text-slate-900">45 minutes</SelectItem>
                      <SelectItem value="60" className="text-slate-900">60 minutes</SelectItem>
                      <SelectItem value="90" className="text-slate-900">90 minutes</SelectItem>
                      <SelectItem value="120" className="text-slate-900">120 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Streaming Type Selection */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
                <Video className="w-5 h-5 mr-2 text-indigo-600" />
                Streaming Method
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* External Link Option */}
                <div
                  onClick={() => setFormData({ ...formData, stream_type: 'external' })}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.stream_type === 'external'
                      ? 'border-indigo-500 bg-white shadow-md'
                      : 'border-slate-200 bg-white/50 hover:border-indigo-300'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      formData.stream_type === 'external' ? 'bg-indigo-100' : 'bg-slate-100'
                    }`}>
                      <Link2 className={`w-5 h-5 ${
                        formData.stream_type === 'external' ? 'text-indigo-600' : 'text-slate-500'
                      }`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">External Link</h4>
                      <p className="text-xs text-slate-500">Zoom, Google Meet, YouTube</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">
                    Use your existing meeting platform. Students will be redirected to your meeting link.
                  </p>
                </div>

                {/* Embedded Video Option */}
                <div
                  onClick={() => setFormData({ ...formData, stream_type: 'embedded' })}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.stream_type === 'embedded'
                      ? 'border-indigo-500 bg-white shadow-md'
                      : 'border-slate-200 bg-white/50 hover:border-indigo-300'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      formData.stream_type === 'embedded' ? 'bg-indigo-100' : 'bg-slate-100'
                    }`}>
                      <Monitor className={`w-5 h-5 ${
                        formData.stream_type === 'embedded' ? 'text-indigo-600' : 'text-slate-500'
                      }`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">In-App Streaming</h4>
                      <p className="text-xs text-slate-500">Built-in video call</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">
                    Stream directly within the app. Includes chat, screen share, and recording.
                  </p>
                </div>
              </div>

              {/* External Link Input */}
              {formData.stream_type === 'external' && (
                <div className="mt-4">
                  <Label htmlFor="external_link" className="text-slate-700 font-medium text-sm mb-2 block">
                    Meeting Link *
                  </Label>
                  <Input
                    id="external_link"
                    data-testid="external-link-input"
                    type="url"
                    placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                    value={formData.external_link}
                    onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                    className="h-12 rounded-lg border-slate-200 focus:border-indigo-500 bg-white text-slate-900"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Paste your Zoom, Google Meet, or YouTube Live link
                  </p>
                </div>
              )}

              {/* Embedded Options */}
              {formData.stream_type === 'embedded' && (
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div className="flex items-center space-x-3">
                      <input 
                        type="checkbox" 
                        id="enable_chat"
                        checked={formData.enable_chat}
                        onChange={(e) => setFormData({ ...formData, enable_chat: e.target.checked })}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                      <label htmlFor="enable_chat" className="text-sm font-medium text-slate-700">
                        Enable Live Chat
                      </label>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div className="flex items-center space-x-3">
                      <input 
                        type="checkbox" 
                        id="enable_screen_share"
                        checked={formData.enable_screen_share}
                        onChange={(e) => setFormData({ ...formData, enable_screen_share: e.target.checked })}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                      <label htmlFor="enable_screen_share" className="text-sm font-medium text-slate-700">
                        Enable Screen Sharing
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/teacher/dashboard')}
                className="rounded-full px-8"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-class-btn"
                disabled={loading}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-8 shadow-lg shadow-indigo-500/20 hover:-translate-y-0.5 transition-all duration-200"
              >
                {loading ? 'Scheduling...' : 'Schedule Live Class'}
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
}
